import { FC, useEffect, useState } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import { TextField } from "@mui/material";
import axios from "../../../utils/axios";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { fetchLocations } from "../../../store/locationStore/locationThunk";

// Reusable Location picker backed by the locationStore.
interface Props {
  value?: any; // locationId or location object
  onChange: (locationId: string) => void;
  label?: string;
  activeOnly?: boolean;
  size?: "small" | "medium";
  // When true, renders as a borderless "c-text-field-3" pill (matches the Items filter bar).
  borderless?: boolean;
  width?: number | string;
  // Location id to hide from the list (e.g. exclude the transfer source from the
  // destination picker so the same location can't be both).
  excludeId?: string;
  allOption?: string; // prepends an "All …" option (value "") and disables the clear button
  // Lists every account location, not just the user's own (e.g. a requisition fulfiller).
  allLocations?: boolean;
}

const LocationSelect: FC<Props> = ({ value, onChange, label = "Location", activeOnly = true, size = "small", borderless = false, width, excludeId, allOption, allLocations = false }) => {
  const dispatch = useAppDispatch();
  const { locations } = useAppSelector((state) => state.locations);
  const [allList, setAllList] = useState<any[] | null>(null);

  useEffect(() => {
    if (!locations || locations.length === 0) dispatch(fetchLocations());
  }, [dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  // Kept out of the store so the user's access-filtered list isn't overwritten.
  useEffect(() => {
    if (!allLocations || allList) return;
    axios.get("/api/v1/erp/locations?scope=all")
      .then((res) => setAllList(res.data || []))
      .catch(() => setAllList([]));
  }, [allLocations, allList]);

  const source: any[] = allLocations ? allList || [] : locations || [];
  const locationOptions = source
    .filter((l) => (activeOnly ? l.isActive !== false : true))
    .filter((l) => (excludeId ? l._id !== excludeId : true))
    .map((l) => ({ label: `${l.name} (${l.code})`, value: l._id }));
  const options = allOption ? [{ label: allOption, value: "" }, ...locationOptions] : locationOptions;

  const currentId = value?._id || value || "";
  const selected = options.find((o) => o.value === currentId) || null;

  return (
    <Autocomplete
      options={options}
      getOptionLabel={(o: any) => o.label || ""}
      isOptionEqualToValue={(o: any, v: any) => o.value === v.value}
      value={selected}
      disableClearable={!!allOption}
      onChange={(_e, v: any) => onChange(v ? v.value : "")}
      style={width ? { width } : undefined}
      renderInput={(params) =>
        borderless ? (
          <TextField
            {...params}
            className="c-text-field-3"
            variant="standard"
            size={size}
            placeholder={label}
            InputProps={{ ...params.InputProps, disableUnderline: true, style: { fontSize: 14 } }}
          />
        ) : (
          <TextField {...params} label={label} size={size} />
        )
      }
    />
  );
};

export default LocationSelect;
